"use client";

import { useEffect, useState } from "react";
import type { Highlighter } from "shiki";

// One highlighter for the whole app; shiki is loaded lazily on first use.

let highlighterPromise: Promise<Highlighter> | null = null;

const LANGS = [
  "javascript",
  "typescript",
  "jsx",
  "tsx",
  "html",
  "css",
  "json",
  "bash",
  "python",
  "markdown",
  "toml",
  "yaml",
  "xml",
  "sql",
  "rust",
  "go",
  "java",
  "php",
  "c",
  "cpp",
  "csharp",
  "dockerfile",
  "ini",
] as const;

const ALIASES: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  py: "python",
  sh: "bash",
  shell: "bash",
  zsh: "bash",
  yml: "yaml",
  md: "markdown",
  docker: "dockerfile",
};

function getHighlighter(): Promise<Highlighter> {
  if (!highlighterPromise) {
    highlighterPromise = import("shiki").then(({ createHighlighter }) =>
      createHighlighter({
        themes: ["github-light", "github-dark"],
        langs: [...LANGS],
      }),
    );
  }
  return highlighterPromise;
}

function resolveLang(highlighter: Highlighter, lang: string): string {
  const wanted = ALIASES[lang.toLowerCase()] ?? lang.toLowerCase();
  return highlighter.getLoadedLanguages().includes(wanted) ? wanted : "text";
}

interface CodeBlockProps {
  code: string;
  lang?: string;
  className?: string;
}

/**
 * Syntax-highlighted code block with light and dark themes driven by the
 * `.dark` class (see `.shiki` rules in globals.css). Falls back to a plain
 * `<pre>` until shiki has loaded, so streaming output is never blank.
 *
 * shiki HTML-escapes all code content, so its output is safe to inject.
 */
export function CodeBlock({ code, lang = "text", className }: CodeBlockProps) {
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getHighlighter()
      .then((highlighter) => {
        if (cancelled) return;
        setHtml(
          highlighter.codeToHtml(code, {
            lang: resolveLang(highlighter, lang),
            themes: { light: "github-light", dark: "github-dark" },
            defaultColor: false,
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setHtml(null);
      });
    return () => {
      cancelled = true;
    };
  }, [code, lang]);

  const wrapper = `code-block relative my-3 overflow-hidden rounded-md border border-border text-[13px] leading-relaxed ${className ?? ""}`;

  if (!html) {
    return (
      <div className={wrapper}>
        <pre className="overflow-x-auto bg-muted p-4 font-mono">
          <code>{code}</code>
        </pre>
      </div>
    );
  }

  return <div className={wrapper} dangerouslySetInnerHTML={{ __html: html }} />;
}
